import React, { useState, useEffect, useCallback } from "react";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Box,
  Alert,
  CircularProgress,
  Input,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  CardMedia,
  Chip,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate, useParams } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CropIcon from "@mui/icons-material/Crop";
import { getHeader } from "../../utils/util";
import { API_ENDPOINT } from "../../constants/constants";
import ToolsLayout from "./ToolsLayout";
import { useQueryClient } from "react-query";
import { categoryNames, invalidateTools, money } from "./toolsData";
import ImageCropDialog from "./ImageCropDialog";
import useApiEp from "../hooks/useApiEp";

// ─── Units ───────────────────────────────────────────────────────────────────
const WEIGHT_UNITS = [
  {
    group: "Mass",
    options: [
      { value: "kg", label: "Kilograms (kg)" },
      { value: "g", label: "Grams (g)" },
      { value: "mg", label: "Milligrams (mg)" },
      { value: "lb", label: "Pounds (lb)" },
      { value: "oz", label: "Ounces (oz)" },
      { value: "t", label: "Metric Tonnes (t)" },
    ],
  },
  {
    group: "Volume / Liquid",
    options: [
      { value: "l", label: "Litres (L)" },
      { value: "ml", label: "Millilitres (mL)" },
      { value: "fl_oz", label: "Fluid Ounces (fl oz)" },
      { value: "gal", label: "Gallons (gal)" },
    ],
  },
  {
    group: "Count / Pack",
    options: [
      { value: "pcs", label: "Pieces (pcs)" },
      { value: "pair", label: "Pair" },
      { value: "set", label: "Set" },
      { value: "pack", label: "Pack" },
      { value: "box", label: "Box" },
      { value: "roll", label: "Roll" },
      { value: "bag", label: "Bag" },
      { value: "bundle", label: "Bundle" },
    ],
  },
];

const CUSTOM_VALUE = "__custom__";

// ─── Styled ───────────────────────────────────────────────────────────────────
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  maxWidth: 1000,
  margin: "0 auto",
  borderRadius: theme.spacing(2),
  border: "1px solid #e4e9e3",
  boxShadow: "none",
  [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(1.5, 3),
  fontSize: "1.1rem",
}));

const SuccessDialogContent = styled(DialogContent)(({ theme }) => ({
  textAlign: "center",
  padding: theme.spacing(4),
}));

const SuccessIcon = styled(CheckCircleIcon)(({ theme }) => ({
  fontSize: 64,
  color: theme.palette.success.main,
  marginBottom: theme.spacing(2),
}));

// ─── Helpers ──────────────────────────────────────────────────────────────────
const isCustomUnit = (unit) => {
  if (!unit) return false;
  return !WEIGHT_UNITS.flatMap((g) => g.options).some((o) => o.value === unit);
};

/**
 * Converts any image URL into a local blob URL so cropperjs never hits CORS.
 * Returns null if fetch fails (instead of falling back to original URL).
 */
const toLocalBlobUrl = async (src) => {
  if (!src) return null;
  if (src.startsWith("blob:") || src.startsWith("data:")) return src;
  try {
    const res = await fetch(src, { mode: "cors" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  } catch (err) {
    console.error("Failed to fetch image for cropping:", err);
    return null; // indicate failure
  }
};

// ─── Component ────────────────────────────────────────────────────────────────
const AdminAddTool = () => {
  const { id } = useParams();
  const categoryQuery = useApiEp(`user/product-categories`);
  const categories = categoryNames(categoryQuery.data);
  const queryClient = useQueryClient();
  const [loadedTool, setLoadedTool] = useState(null);
  const [loadVersion, setLoadVersion] = useState(0);
  const [categoryDialog, setCategoryDialog] = useState(false);
  const [categoryName, setCategoryName] = useState("");
  const [categorySaving, setCategorySaving] = useState(false);
  const [categoryError, setCategoryError] = useState("");
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    mrp: "",
    sellingPrice: "",
    discount: "",
    category: "",
    stock: "",
    gst: "",
    weight: "",
    weightUnit: "kg",
  });

  const [customUnit, setCustomUnit] = useState("");
  const [showCustomUnit, setShowCustomUnit] = useState(false);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");

  // ── Crop state ───────────────────────────────────────────────────────────
  const [cropQueue, setCropQueue] = useState([]);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [cropSource, setCropSource] = useState(null);
  const [recropTargetId, setRecropTargetId] = useState(null);
  const [cropBatchTotal, setCropBatchTotal] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);

  // ── Load tool for edit mode ───────────────────────────────────────────────
  useEffect(() => {
    const controller = new AbortController();
    setLoadedTool(null);
    if (!id) {
      setFormData({
        name: "",
        description: "",
        mrp: "",
        sellingPrice: "",
        discount: "",
        category: "",
        stock: "",
        gst: "",
        weight: "",
        weightUnit: "kg",
      });
      setCustomUnit("");
      setShowCustomUnit(false);
      setImagePreviews([]);
      setError("");
      setSuccess("");
      setFetchLoading(false);
      return () => controller.abort();
    }
    const fetchTool = async () => {
      setFetchLoading(true);
      setError("");
      try {
        const authHeaders = getHeader ? getHeader() : {};
        const res = await fetch(`${API_ENDPOINT}admin/tool/${encodeURIComponent(id)}`, {
          signal: controller.signal,
          headers: authHeaders.headers || authHeaders,
        });
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        const data = await res.json();
        if (controller.signal.aborted) return;
        if (!data) throw new Error("Tool not found!");
        setLoadedTool(data);

        const loadedUnit = data.weightUnit || "kg";
        const isCustom = isCustomUnit(loadedUnit);

        setFormData({
          name: data.name || "",
          description: data.description || "",
          mrp: data.mrp?.toString() || "",
          sellingPrice: data.sellingPrice?.toString() || "",
          discount:
            Number(data.mrp) > 0
              ? (((Number(data.mrp) - Number(data.sellingPrice)) / Number(data.mrp)) * 100).toFixed(
                  2,
                )
              : "",
          category: data.category || "",
          stock: data.stock?.toString() || "",
          gst: data.gst?.toString() || "",
          weight: data.weight?.toString() || "",
          weightUnit: isCustom ? CUSTOM_VALUE : loadedUnit,
        });

        setCustomUnit(isCustom ? loadedUnit : "");
        setShowCustomUnit(isCustom);

        const initialPreviews = Array.isArray(data.images)
          ? data.images.map((url, idx) => ({
              id: `existing-${idx}-${Date.now()}`,
              preview: url,
              isNew: false,
            }))
          : data.imageUrl
            ? [{ id: "existing", preview: data.imageUrl, isNew: false }]
            : [];

        setImagePreviews(initialPreviews);
      } catch (err) {
        if (err.name === "AbortError") return;
        const msg = err.message || "Failed to fetch tool.";
        setError(msg);
        setDialogMessage(msg);
      } finally {
        if (!controller.signal.aborted) setFetchLoading(false);
      }
    };
    fetchTool();
    return () => controller.abort();
  }, [id, loadVersion]);

  // ── Form handlers ────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => {
      const next = { ...p, [name]: value };
      if (name === "mrp" || name === "sellingPrice") {
        next.discount =
          Number(next.mrp) > 0 &&
          Number(next.sellingPrice) > 0 &&
          Number(next.sellingPrice) <= Number(next.mrp)
            ? (((Number(next.mrp) - Number(next.sellingPrice)) / Number(next.mrp)) * 100).toFixed(2)
            : "";
      }
      return next;
    });
    if (error) setError("");
  };

  const handleWeightUnitChange = (e) => {
    const value = e.target.value;
    if (value === CUSTOM_VALUE) {
      setShowCustomUnit(true);
      setFormData((p) => ({ ...p, weightUnit: CUSTOM_VALUE }));
    } else {
      setShowCustomUnit(false);
      setCustomUnit("");
      setFormData((p) => ({ ...p, weightUnit: value }));
    }
    if (error) setError("");
  };

  const resolvedWeightUnit = () =>
    formData.weightUnit === CUSTOM_VALUE ? customUnit.trim() : formData.weightUnit;

  // ── Image file selection ──────────────────────────────────────────────────
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setError("");

    const validFiles = [];
    const invalidReasons = [];

    for (const file of files) {
      if (!file.type.startsWith("image/")) {
        invalidReasons.push(`${file.name}: not an image`);
        continue;
      }
      if (file.size > 5 * 1024 * 1024) {
        invalidReasons.push(`${file.name}: exceeds 5 MB`);
        continue;
      }
      validFiles.push(file);
    }

    if (invalidReasons.length) setError(`Some files skipped: ${invalidReasons.join(", ")}`);

    if (validFiles.length) {
      setPendingCount((n) => n + validFiles.length);
      setCropQueue((prev) => {
        const next = [...prev, ...validFiles.map((file) => ({ file }))];
        if (prev.length === 0) setCropBatchTotal(next.length);
        else setCropBatchTotal((t) => t + validFiles.length);
        return next;
      });
    }

    e.target.value = "";
  };

  // ── Open cropper for next queued file ─────────────────────────────────────
  useEffect(() => {
    if (cropDialogOpen || cropQueue.length === 0 || recropTargetId) return;

    const next = cropQueue[0];
    const objectUrl = URL.createObjectURL(next.file);
    setCropSource(objectUrl);
    setCropDialogOpen(true);
  }, [cropQueue, cropDialogOpen, recropTargetId]);

  // ── Crop dialog: SAVE ────────────────────────────────────────────────────
  const handleCropSave = useCallback(
    (croppedFile) => {
      const previewUrl = URL.createObjectURL(croppedFile);

      if (recropTargetId) {
        // Recrop case – always close after apply
        setImagePreviews((prev) =>
          prev.map((img) => {
            if (img.id !== recropTargetId) return img;
            if (img.isNew && img.preview?.startsWith("blob:")) {
              try {
                URL.revokeObjectURL(img.preview);
              } catch {}
            }
            return {
              ...img,
              file: croppedFile,
              preview: previewUrl,
              isNew: true,
            };
          }),
        );
        setRecropTargetId(null);
        _closeCropDialog();
      } else {
        // Batch crop case
        setImagePreviews((prev) => [
          ...prev,
          {
            id: Date.now() + Math.floor(Math.random() * 1_000_000),
            file: croppedFile,
            preview: previewUrl,
            isNew: true,
          },
        ]);
        setPendingCount((n) => Math.max(0, n - 1));

        setCropQueue((prevQueue) => {
          const newQueue = prevQueue.slice(1);
          if (newQueue.length > 0) {
            const nextUrl = URL.createObjectURL(newQueue[0].file);
            setCropSource(nextUrl);
            return newQueue;
          } else {
            _closeCropDialog();
            return newQueue;
          }
        });
      }
    },
    [recropTargetId],
  );

  // ── Crop dialog: SKIP ─────────────────────────────────────────────────────
  const handleCropSkip = useCallback(() => {
    if (recropTargetId) {
      setRecropTargetId(null);
      _closeCropDialog();
    } else {
      setPendingCount((n) => Math.max(0, n - 1));
      setCropQueue((prevQueue) => {
        const newQueue = prevQueue.slice(1);
        if (newQueue.length > 0) {
          const nextUrl = URL.createObjectURL(newQueue[0].file);
          setCropSource(nextUrl);
          return newQueue;
        } else {
          _closeCropDialog();
          return newQueue;
        }
      });
    }
  }, [recropTargetId]);

  // ── Crop dialog: CANCEL ALL ───────────────────────────────────────────────
  const handleCropCancelAll = useCallback(() => {
    setCropQueue([]);
    setPendingCount(0);
    setCropBatchTotal(0);
    setRecropTargetId(null);
    _closeCropDialog();
  }, []);

  const _closeCropDialog = () => {
    setCropDialogOpen(false);
    // Do NOT revoke cropSource here – it will be cleaned in onExited
  };

  // Cleanup blob URL after dialog exit
  const handleCropDialogExited = useCallback(() => {
    if (cropSource?.startsWith("blob:")) {
      URL.revokeObjectURL(cropSource);
      setCropSource(null);
    }
  }, [cropSource]);

  // ── Re-crop existing image ─────────────────────────────────────────────────
  const handleRecropExisting = async (img) => {
    setError("");
    const resolvedUrl = await toLocalBlobUrl(img.preview);
    if (!resolvedUrl) {
      setError("Could not load image for cropping. Check network or CORS settings.");
      return;
    }
    setRecropTargetId(img.id);
    setCropSource(resolvedUrl);
    setCropDialogOpen(true);
  };

  // ── Remove image ──────────────────────────────────────────────────────────
  const handleRemoveImage = (id) => {
    setImagePreviews((prev) => {
      const removed = prev.find((img) => img.id === id);
      if (removed?.isNew && removed.preview?.startsWith("blob:")) {
        try {
          URL.revokeObjectURL(removed.preview);
        } catch {}
      }
      return prev.filter((img) => img.id !== id);
    });
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading || fetchLoading || cropQueue.length || cropDialogOpen || (id && !loadedTool))
      return;
    setLoading(true);
    setError("");
    setSuccess("");

    if (
      !formData.name.trim() ||
      !formData.description.trim() ||
      !formData.mrp ||
      !formData.sellingPrice ||
      !formData.category ||
      formData.stock === ""
    )
      return _formError("Please fill in all required fields.");
    if (imagePreviews.length === 0) return _formError("At least one image is required.");
    if (
      !Number.isFinite(Number(formData.mrp)) ||
      !Number.isFinite(Number(formData.sellingPrice)) ||
      !Number.isSafeInteger(Number(formData.stock)) ||
      parseFloat(formData.mrp) <= 0 ||
      parseFloat(formData.sellingPrice) <= 0 ||
      Number(formData.stock) < 0
    )
      return _formError(
        "MRP and selling price must be positive numbers. Stock must be a whole number of zero or more.",
      );
    if (parseFloat(formData.sellingPrice) > parseFloat(formData.mrp))
      return _formError("Selling price must be less than or equal to MRP.");
    if (
      formData.gst &&
      (isNaN(formData.gst) || parseFloat(formData.gst) < 0 || parseFloat(formData.gst) > 100)
    )
      return _formError("GST must be a number between 0 and 100.");
    if (formData.weight && (isNaN(formData.weight) || parseFloat(formData.weight) <= 0))
      return _formError("Weight must be a positive number.");

    const finalUnit = resolvedWeightUnit();
    if (formData.weightUnit === CUSTOM_VALUE && !finalUnit)
      return _formError("Please enter a custom unit or select one from the list.");

    const fd = new FormData();
    fd.append("name", formData.name.trim());
    fd.append("description", formData.description.trim());
    fd.append("mrp", formData.mrp);
    fd.append("sellingPrice", formData.sellingPrice);
    fd.append(
      "discount",
      (
        ((Number(formData.mrp) - Number(formData.sellingPrice)) / Number(formData.mrp)) *
        100
      ).toFixed(2),
    );
    fd.append("category", formData.category);
    fd.append("stock", formData.stock);
    fd.append("gst", formData.gst || "");
    fd.append("weight", formData.weight || "");
    fd.append("weightUnit", finalUnit || "kg");

    const keptUrls = imagePreviews.filter((i) => !i.isNew).map((i) => i.preview);
    fd.append("keptImageUrls", JSON.stringify(keptUrls));
    imagePreviews.filter((i) => i.isNew && i.file).forEach((i) => fd.append("image", i.file));

    try {
      if (id) {
        const latestResponse = await fetch(
          `${API_ENDPOINT}admin/tool/${encodeURIComponent(id)}`,
          getHeader(),
        );
        if (!latestResponse.ok)
          throw new Error("Unable to verify the product before saving. Please try again.");
        const latest = await latestResponse.json();
        if (
          Number(latest.stock) !== Number(loadedTool.stock) ||
          (latest.updatedOn && loadedTool.updatedOn && latest.updatedOn !== loadedTool.updatedOn)
        ) {
          throw new Error(
            "This product changed while you were editing. Reopen it to review the latest details before saving.",
          );
        }
      }
      const endpoint = id ? `admin/edit-tool/${id}` : "admin/add-tool";
      const authHeaders = getHeader ? getHeader() : {};
      const headers = {};
      if (authHeaders.headers) {
        Object.keys(authHeaders.headers).forEach((key) => {
          if (key.toLowerCase() !== "content-type") headers[key] = authHeaders.headers[key];
        });
      }

      const response = await fetch(`${API_ENDPOINT}${endpoint}`, {
        method: "POST",
        body: fd,
        headers,
      });

      if (!response.ok) {
        let msg = `HTTP error! status: ${response.status}`;
        try {
          const errData = await response.json();
          msg = errData.message || msg;
          if (errData.status === "validation_error" && errData.errors)
            msg = errData.errors.map((e) => `${e.field}: ${e.message}`).join("; ");
        } catch {}
        throw new Error(msg);
      }

      const isEditMode = !!id;
      await invalidateTools(queryClient);
      setSuccess(isEditMode ? "Tool updated successfully!" : "Tool added successfully!");
      setDialogMessage(
        isEditMode ? "Tool has been updated successfully!" : "Tool has been added successfully!",
      );
      setShowSuccessDialog(true);

      if (!isEditMode) {
        imagePreviews.forEach((img) => {
          if (img.isNew && img.preview?.startsWith("blob:"))
            try {
              URL.revokeObjectURL(img.preview);
            } catch {}
        });
        setImagePreviews([]);
        setCustomUnit("");
        setShowCustomUnit(false);
        setFormData({
          name: "",
          description: "",
          mrp: "",
          sellingPrice: "",
          discount: "",
          category: "",
          stock: "",
          gst: "",
          weight: "",
          weightUnit: "kg",
        });
      }
    } catch (err) {
      const msg = err.message || "Failed to save tool. Please try again.";
      setError(msg);
      setDialogMessage(msg);
    } finally {
      setLoading(false);
    }
  };

  const _formError = (msg) => {
    setError(msg);
    setDialogMessage(msg);
    setLoading(false);
  };

  const handleSuccessClose = () => {
    setShowSuccessDialog(false);
    navigate("/home/tools");
  };

  const createCategory = async (event) => {
    event.preventDefault();
    const name = categoryName.trim();
    if (categorySaving || !name) return;
    if (categories.some((category) => category.toLowerCase() === name.toLowerCase())) {
      setCategoryError("This category already exists. Select it from the list.");
      return;
    }
    setCategorySaving(true);
    setCategoryError("");
    try {
      const body = new FormData();
      body.append("category", name);
      const headers = { ...getHeader().headers };
      Object.keys(headers).forEach((key) => {
        if (key.toLowerCase() === "content-type") delete headers[key];
      });
      const response = await fetch(`${API_ENDPOINT}user/add-product-category`, {
        method: "POST",
        body,
        headers,
      });
      if (!response.ok) throw new Error("Unable to create the category. Please try again.");
      await invalidateTools(queryClient);
      setFormData((current) => ({ ...current, category: name }));
      setCategoryDialog(false);
      setCategoryName("");
    } catch (err) {
      setCategoryError(err.message);
    } finally {
      setCategorySaving(false);
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const isEdit = !!id;
  const pageTitle = isEdit ? "Edit product" : "Add product";
  const submitText = isEdit ? "Save changes" : "Create product";
  const submitLoadingText = isEdit ? "Updating…" : "Adding…";
  const queueRemaining = cropQueue.length;
  const cropProcessed = cropBatchTotal - queueRemaining;

  return (
    <ToolsLayout
      title={pageTitle}
      description={
        isEdit
          ? "Update product information, pricing, and stock."
          : "Build your catalogue with accurate product details and opening stock."
      }
    >
      <StyledPaper elevation={3}>
        <Typography variant="h6" component="h2" gutterBottom color="primary">
          Product details
        </Typography>
        <Typography variant="body1" color="textSecondary" paragraph>
          {isEdit
            ? "Review the details below before saving your changes."
            : "Enter a name, description, category, prices, and opening stock. Add at least one product image."}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Use clear product images. Square crops work well in the customer app.
        </Typography>

        {error && (
          <Alert
            severity="error"
            sx={{ mt: 2 }}
            action={
              id &&
              !loadedTool && (
                <Button color="inherit" onClick={() => setLoadVersion((value) => value + 1)}>
                  Retry
                </Button>
              )
            }
          >
            {error}
          </Alert>
        )}
        {categoryQuery.isError && (
          <Alert
            severity="warning"
            sx={{ mt: 2 }}
            action={
              <Button onClick={categoryQuery.refetch} color="inherit">
                Retry
              </Button>
            }
          >
            Categories could not be loaded. Retry before selecting a new category.
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {success}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <fieldset
            disabled={loading || fetchLoading || (isEdit && !loadedTool)}
            style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}
          >
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Product Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  multiline
                  rows={4}
                  required
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined" required>
                  <InputLabel id="product-category-label">Category</InputLabel>
                  <Select
                    name="category"
                    labelId="product-category-label"
                    value={formData.category}
                    onChange={(e) => {
                      if (e.target.value === "__add_new__") {
                        setCategoryDialog(true);
                        setCategoryError("");
                        return;
                      }
                      handleChange(e);
                    }}
                    label="Category"
                  >
                    {[
                      ...new Set([
                        ...categories,
                        ...(formData.category ? [formData.category] : []),
                      ]),
                    ].map((cat) => (
                      <MenuItem key={cat} value={cat}>
                        {cat}
                      </MenuItem>
                    ))}
                    <MenuItem disabled>
                      <em>──────────</em>
                    </MenuItem>
                    <MenuItem value="__add_new__" sx={{ color: "primary.main", fontWeight: 600 }}>
                      + Add category
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography component="h2" variant="h6" className="tools-form-section">
                  Pricing
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Discount is calculated from MRP and selling price.
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="MRP (₹)"
                  name="mrp"
                  type="number"
                  inputProps={{ step: "0.01", min: "0" }}
                  value={formData.mrp}
                  onChange={handleChange}
                  required
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Selling Price (₹)"
                  name="sellingPrice"
                  type="number"
                  inputProps={{ step: "0.01", min: "0" }}
                  value={formData.sellingPrice}
                  onChange={handleChange}
                  required
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Discount (%)"
                  name="discount"
                  type="number"
                  inputProps={{ step: "0.01", min: "0", max: "100" }}
                  value={formData.discount}
                  onChange={handleChange}
                  InputProps={{ readOnly: true }}
                  helperText="Calculated automatically"
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="GST (%)"
                  name="gst"
                  type="number"
                  inputProps={{ step: "0.01", min: "0", max: "100" }}
                  value={formData.gst}
                  onChange={handleChange}
                  variant="outlined"
                  helperText="Optional"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography component="h2" variant="h6" className="tools-form-section">
                  Inventory & packaging
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Set available units and the weight or size of each product.
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label={isEdit ? "Stock on hand" : "Opening stock"}
                  name="stock"
                  type="number"
                  inputProps={{ min: "0", step: "1" }}
                  value={formData.stock}
                  onChange={handleChange}
                  required
                  helperText="Whole units. Enter 0 for an out-of-stock product."
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Weight"
                  name="weight"
                  type="number"
                  inputProps={{ step: "0.01", min: "0" }}
                  value={formData.weight}
                  onChange={handleChange}
                  variant="outlined"
                  helperText="Optional"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="product-unit-label">Unit</InputLabel>
                  <Select
                    name="weightUnit"
                    labelId="product-unit-label"
                    value={formData.weightUnit}
                    onChange={handleWeightUnitChange}
                    label="Unit"
                  >
                    {WEIGHT_UNITS.map((group) => [
                      <MenuItem
                        key={`hdr-${group.group}`}
                        disabled
                        sx={{
                          opacity: 0.6,
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          letterSpacing: 1,
                        }}
                      >
                        {group.group.toUpperCase()}
                      </MenuItem>,
                      ...group.options.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      )),
                    ])}
                    <MenuItem disabled>
                      <em>──────────</em>
                    </MenuItem>
                    <MenuItem value={CUSTOM_VALUE} sx={{ color: "primary.main", fontWeight: 600 }}>
                      ✏️ Type custom unit…
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {showCustomUnit && (
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Custom Unit"
                    value={customUnit}
                    onChange={(e) => {
                      setCustomUnit(e.target.value);
                      if (error) setError("");
                    }}
                    variant="outlined"
                    placeholder="e.g. dozen, carton, sqm…"
                    helperText="Enter your unit label"
                    inputProps={{ maxLength: 30 }}
                  />
                </Grid>
              )}

              {/* ── Images ─────────────────────────────────────────────────── */}
              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <Typography variant="h6" color="primary">
                    Product images
                  </Typography>
                  {imagePreviews.length > 0 && (
                    <Chip
                      label={`${imagePreviews.length} image${
                        imagePreviews.length !== 1 ? "s" : ""
                      }`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                  {pendingCount > 0 && (
                    <Chip
                      label={`${pendingCount} pending crop`}
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                  )}
                </Box>

                {/* Batch progress */}
                {cropBatchTotal > 1 && queueRemaining > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 0.5,
                      }}
                    >
                      <Typography variant="caption" color="textSecondary">
                        Cropping images… ({cropProcessed}/{cropBatchTotal})
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {Math.round((cropProcessed / cropBatchTotal) * 100)}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.round((cropProcessed / cropBatchTotal) * 100)}
                      sx={{ borderRadius: 1 }}
                    />
                  </Box>
                )}

                {/* Preview grid */}
                {(imagePreviews.length > 0 || pendingCount > 0) && (
                  <Box sx={{ mb: 3 }}>
                    <Grid container spacing={2}>
                      {imagePreviews.map((img, index) => (
                        <Grid item xs={6} sm={4} md={3} key={img.id}>
                          <Box
                            sx={{
                              position: "relative",
                              borderRadius: 1,
                              overflow: "hidden",
                              border: (t) => `2px solid ${t.palette.divider}`,
                              "&:hover .image-actions, &:focus-within .image-actions": {
                                opacity: 1,
                              },
                            }}
                          >
                            <CardMedia
                              component="img"
                              src={img.preview}
                              alt={`Tool image ${index + 1}`}
                              sx={{
                                width: "100%",
                                aspectRatio: "1",
                                objectFit: "cover",
                                display: "block",
                              }}
                            />
                            <Box
                              className="image-actions"
                              sx={{
                                position: "absolute",
                                inset: 0,
                                background: "rgba(0,0,0,0.45)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 1,
                                opacity: { xs: 1, sm: 0 },
                                transition: "opacity 0.2s",
                              }}
                            >
                              <Tooltip title="Recrop / Replace">
                                <IconButton
                                  size="small"
                                  aria-label={`Crop product image ${index + 1}`}
                                  onClick={() => handleRecropExisting(img)}
                                  sx={{
                                    bgcolor: "white",
                                    "&:hover": { bgcolor: "#e3f2fd" },
                                  }}
                                >
                                  <CropIcon fontSize="small" color="primary" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Remove">
                                <IconButton
                                  size="small"
                                  aria-label={`Remove product image ${index + 1}`}
                                  onClick={() => handleRemoveImage(img.id)}
                                  sx={{
                                    bgcolor: "white",
                                    "&:hover": { bgcolor: "#ffebee" },
                                  }}
                                >
                                  <CloseIcon fontSize="small" color="error" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                            <Box
                              sx={{
                                position: "absolute",
                                bottom: 4,
                                left: 4,
                                bgcolor: "rgba(0,0,0,0.55)",
                                color: "white",
                                borderRadius: "4px",
                                px: 0.75,
                                py: 0.25,
                                fontSize: "0.65rem",
                                fontWeight: 700,
                                lineHeight: 1.4,
                              }}
                            >
                              #{index + 1}
                            </Box>
                          </Box>
                        </Grid>
                      ))}

                      {/* Pending placeholders */}
                      {Array.from({ length: pendingCount }).map((_, i) => (
                        <Grid item xs={6} sm={4} md={3} key={`pending-${i}`}>
                          <Box
                            sx={{
                              width: "100%",
                              aspectRatio: "1",
                              borderRadius: 1,
                              border: "2px dashed",
                              borderColor: "warning.main",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 0.5,
                              bgcolor: "warning.50",
                              opacity: 0.8,
                            }}
                          >
                            <CropIcon sx={{ color: "warning.main", fontSize: 28 }} />
                            <Typography variant="caption" color="warning.dark" fontWeight={600}>
                              Awaiting crop
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                {/* Upload zone */}
                <Box
                  sx={{
                    border: "2px dashed",
                    borderColor: "primary.light",
                    borderRadius: 2,
                    p: 3,
                    textAlign: "center",
                    bgcolor: "primary.50",
                    cursor: "pointer",
                    "&:hover": {
                      borderColor: "primary.main",
                      bgcolor: "primary.100",
                    },
                    transition: "all 0.2s",
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label="Upload product images"
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      document.getElementById("image-upload-input").click();
                    }
                  }}
                  onClick={() => document.getElementById("image-upload-input").click()}
                >
                  <Typography variant="body1" color="primary" fontWeight={600} gutterBottom>
                    📷 Click to upload images
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    JPG / PNG · Up to 5 MB each · Multiple selection supported
                  </Typography>
                  <Input
                    id="image-upload-input"
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageChange}
                    sx={{ display: "none" }}
                    inputProps={{ "aria-label": "Upload images" }}
                  />
                </Box>

                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ display: "block", mt: 1 }}
                >
                  Each image opens in a cropper for framing. Hover an existing image to recrop or
                  remove it.
                </Typography>
              </Grid>
            </Grid>
          </fieldset>

          <Box className="tools-save-bar">
            <Box
              sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}
            >
              <Typography variant="body2">
                Selling price: <strong>{money(formData.sellingPrice)}</strong> · Stock:{" "}
                <strong>{formData.stock === "" ? "—" : formData.stock} units</strong>
              </Typography>
              <Button disabled={loading} onClick={() => navigate("/home/tools")}>
                Cancel
              </Button>
            </Box>
            <SubmitButton
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={
                loading ||
                fetchLoading ||
                queueRemaining > 0 ||
                cropDialogOpen ||
                (isEdit && !loadedTool) ||
                showSuccessDialog
              }
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {queueRemaining > 0
                ? `Please finish cropping (${queueRemaining} remaining)…`
                : loading
                  ? submitLoadingText
                  : submitText}
            </SubmitButton>
          </Box>
        </Box>
      </StyledPaper>

      <Dialog
        open={categoryDialog}
        onClose={categorySaving ? undefined : () => setCategoryDialog(false)}
        fullWidth
        maxWidth="xs"
      >
        <Box component="form" onSubmit={createCategory}>
          <DialogTitle>Add category</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Create a category while keeping your product details.
            </Typography>
            <TextField
              autoFocus
              required
              fullWidth
              label="Category name"
              value={categoryName}
              onChange={(event) => setCategoryName(event.target.value)}
              disabled={categorySaving}
              sx={{ mt: 1 }}
            />
            {categoryError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {categoryError}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button disabled={categorySaving} onClick={() => setCategoryDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              disabled={categorySaving || !categoryName.trim()}
            >
              {categorySaving ? "Creating…" : "Create category"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* ── Crop dialog ──────────────────────────────────────────────────── */}
      <ImageCropDialog
        open={cropDialogOpen}
        initialSrc={cropSource}
        loading={false}
        isRecrop={!!recropTargetId}
        queueIndex={recropTargetId ? null : cropProcessed}
        queueTotal={recropTargetId ? null : cropBatchTotal}
        onSave={handleCropSave}
        onSkip={handleCropSkip}
        onCancelAll={handleCropCancelAll}
        onExited={handleCropDialogExited} // new cleanup
      />

      {/* ── Success dialog ───────────────────────────────────────────────── */}
      <Dialog open={showSuccessDialog} onClose={handleSuccessClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ textAlign: "center" }}>Success!</DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleSuccessClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (t) => t.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <SuccessDialogContent>
          <SuccessIcon />
          <DialogContentText component="div">
            <Typography variant="h6" gutterBottom>
              {isEdit ? "Product saved" : "Product created"}
            </Typography>
            <Typography variant="body1" color="textSecondary">
              {dialogMessage}
            </Typography>
          </DialogContentText>
        </SuccessDialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button variant="contained" color="primary" onClick={handleSuccessClose} size="large">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </ToolsLayout>
  );
};

export default AdminAddTool;
